import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import CloudAuthService from './cloudAuthService';
import { toast } from 'react-hot-toast';
import { resumePendingDownloadAfterAuth } from '../../../utils/downloadResume';
import { humanizeSsoError, ProviderMark } from './providerIcons';
import { sanitizeProviderId, scrubSsoCallbackUrl } from './ssoAgentClient';

const VERIFIED_HOLD_MS = 1800;

const providerLabel = (id) => {
  const map = {
    google: 'Google',
    github: 'GitHub',
    microsoft: 'Microsoft',
    gitlab: 'GitLab',
    yahoo: 'Yahoo',
  };
  return map[id] || (id ? id.charAt(0).toUpperCase() + id.slice(1) : 'SSO');
};

/**
 * Central SSO Agent redirect landing.
 * Query: ?code=<exchange>&provider=... or ?error=<stable_code>
 * Shows verifying → verified (professional) → dashboard.
 */
export default function CloudAuthCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithCloud } = useAuth();
  const [phase, setPhase] = useState('verifying'); // verifying | verified | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [welcomeName, setWelcomeName] = useState('');
  const [provider, setProvider] = useState(() => {
    try {
      return sanitizeProviderId(sessionStorage.getItem('sso_auth_provider')) || '';
    } catch {
      return '';
    }
  });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return undefined;
    ran.current = true;
    let active = true;
    let redirectTimer;

    async function processCallback() {
      try {
        const search =
          location.search ||
          (location.hash.includes('?')
            ? location.hash.substring(location.hash.indexOf('?'))
            : '');
        const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
        const hash = location.hash.includes('#')
          ? location.hash.substring(location.hash.indexOf('#') + 1)
          : '';
        const hashParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : hash);
        hashParams.forEach((v, k) => {
          if (!params.has(k)) params.set(k, v);
        });

        params.delete('api_key');
        params.delete('token');
        params.delete('access_token');
        params.delete('id_token');
        params.delete('refresh_token');

        const code = params.get('code');
        const oauthState = params.get('state');
        const error = params.get('error') || params.get('error_description');
        const providerId =
          sanitizeProviderId(params.get('provider')) ||
          sanitizeProviderId(
            (() => {
              try {
                return sessionStorage.getItem('sso_auth_provider');
              } catch {
                return null;
              }
            })()
          ) ||
          'sso';
        if (active) setProvider(providerId);

        if (error) {
          throw new Error(String(error).slice(0, 80));
        }
        if (!code) {
          throw new Error('invalid_request');
        }

        // ***** Code Start BH17082026_1035 - Pass OAuth state into Agent callback completion
        const session = await CloudAuthService.handleCloudCallback(
          providerId,
          code,
          oauthState,
          null
        );
        // ***** Code End BH17082026_1035

        if (!session?.cookieSession && !session?.apiKey) {
          throw new Error('account_error');
        }

        if (loginWithCloud) {
          await loginWithCloud(session);
        }

        scrubSsoCallbackUrl();

        const name = session.user?.first_name || session.email || '';
        const safeName =
          typeof name === 'string' && name.length < 40 && !/[<>]/.test(name) ? name : '';
        if (active) {
          setWelcomeName(safeName);
          setPhase('verified');
        }

        try {
          const downloaded = await resumePendingDownloadAfterAuth();
          if (downloaded) {
            toast.success('Candor IDE download resumed');
          }
        } catch (_) {
          /* non-blocking */
        }

        redirectTimer = setTimeout(() => {
          if (!active) return;
          window.location.assign(`${window.location.origin}/dashboard`);
        }, VERIFIED_HOLD_MS);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[SSO] callback failed:', err?.message || 'unknown');
        }
        scrubSsoCallbackUrl();
        const message = humanizeSsoError(err?.message || 'authentication_failed');
        if (active) {
          setErrorMsg(message);
          setPhase('error');
        }
        toast.error(message);
        redirectTimer = setTimeout(() => {
          if (active) navigate('/login', { replace: true });
        }, 3600);
      }
    }

    processCallback();
    return () => {
      active = false;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [location, navigate, loginWithCloud]);

  const pid = sanitizeProviderId(provider) || '';
  const label = providerLabel(pid);

  return (
    <div className="candor-sso-cb">
      <style>{SSO_CB_CSS}</style>

      <div className="candor-sso-cb__bg" aria-hidden />
      <div className="candor-sso-cb__grid" aria-hidden />

      <main className="candor-sso-cb__stage">
        <header className="candor-sso-cb__brand">
          <img
            src={`${process.env.PUBLIC_URL || ''}/candorlogo.png`}
            alt="Candor"
            className="candor-sso-cb__logo"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <p className="candor-sso-cb__eyebrow">Candor SI · Secure sign-in</p>
        </header>

        <section
          className={`candor-sso-cb__panel candor-sso-cb__panel--${phase}`}
          aria-live="polite"
          aria-busy={phase === 'verifying'}
        >
          {phase === 'verifying' && (
            <>
              <div className="candor-sso-cb__marks">
                {pid ? (
                  <div className="candor-sso-cb__provider" title={label}>
                    <ProviderMark id={pid} className="candor-sso-cb__provider-icon" />
                  </div>
                ) : null}
                <div className="candor-sso-cb__spinner" role="status" aria-label="Verifying" />
              </div>
              <h1 className="candor-sso-cb__title">Verifying your identity</h1>
              <p className="candor-sso-cb__copy">
                Confirming your {label} sign-in with Candor Central SSO.
                <br />
                This only takes a moment.
              </p>
              <ol className="candor-sso-cb__steps">
                <li className="is-done">Provider authenticated</li>
                <li className="is-active">Exchanging secure session</li>
                <li>Opening workspace</li>
              </ol>
            </>
          )}

          {phase === 'verified' && (
            <>
              <div className="candor-sso-cb__success-ring" aria-hidden>
                <svg className="candor-sso-cb__check" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="22" className="candor-sso-cb__check-ring" />
                  <path
                    className="candor-sso-cb__check-mark"
                    d="M14 24.5l6.5 6.5L34 17"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h1 className="candor-sso-cb__title candor-sso-cb__title--ok">Identity verified</h1>
              <p className="candor-sso-cb__copy">
                {welcomeName
                  ? `Welcome back, ${welcomeName}. Your Candor session is ready.`
                  : 'Your Candor session is ready.'}
              </p>
              <p className="candor-sso-cb__next">Opening your dashboard…</p>
              <div className="candor-sso-cb__bar" aria-hidden>
                <span />
              </div>
            </>
          )}

          {phase === 'error' && (
            <>
              <div className="candor-sso-cb__error-mark" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
              </div>
              <h1 className="candor-sso-cb__title candor-sso-cb__title--err">
                Sign-in unsuccessful
              </h1>
              <p className="candor-sso-cb__copy candor-sso-cb__copy--err">{errorMsg}</p>
              <p className="candor-sso-cb__next">Returning you to sign in…</p>
              <button
                type="button"
                className="candor-sso-cb__btn"
                onClick={() => navigate('/login', { replace: true })}
              >
                Back to sign in
              </button>
            </>
          )}
        </section>

        <footer className="candor-sso-cb__foot">
          Encrypted handoff · No passwords shared with Candor in this step
        </footer>
      </main>
    </div>
  );
}

const SSO_CB_CSS = `
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,500;8..60,600&display=swap");
.candor-sso-cb {
  --cb-ink: #f1f5f9;
  --cb-muted: #94a3b8;
  --cb-line: rgba(148, 163, 184, 0.18);
  --cb-accent: #38bdf8;
  --cb-ok: #34d399;
  --cb-err: #f87171;
  --cb-panel: rgba(15, 23, 42, 0.55);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem;
  position: relative;
  overflow: hidden;
  color: var(--cb-ink);
  font-family: "Outfit", "Segoe UI", sans-serif;
  background: #070b14;
}
.candor-sso-cb__bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 90% 55% at 50% -15%, rgba(56, 189, 248, 0.16), transparent 55%),
    radial-gradient(ellipse 45% 35% at 90% 85%, rgba(52, 211, 153, 0.08), transparent 50%),
    linear-gradient(165deg, #070b14 0%, #0f172a 48%, #0a1220 100%);
}
.candor-sso-cb__grid {
  position: absolute;
  inset: 0;
  opacity: 0.22;
  background-image:
    linear-gradient(rgba(148,163,184,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148,163,184,0.07) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 20%, transparent 75%);
}
.candor-sso-cb__stage {
  position: relative;
  width: 100%;
  max-width: 28rem;
  z-index: 1;
  animation: candorSsoIn 0.55s ease-out both;
}
@keyframes candorSsoIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
.candor-sso-cb__brand {
  text-align: center;
  margin-bottom: 1.75rem;
}
.candor-sso-cb__logo {
  height: 2.75rem;
  margin: 0 auto;
  display: block;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.96;
}
.candor-sso-cb__eyebrow {
  margin: 0.85rem 0 0;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--cb-muted);
}
.candor-sso-cb__panel {
  border: 1px solid var(--cb-line);
  border-radius: 1.25rem;
  background: var(--cb-panel);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  padding: 2.25rem 1.75rem 2rem;
  text-align: center;
  box-shadow: 0 24px 48px rgba(0,0,0,0.35);
}
.candor-sso-cb__panel--verified {
  border-color: rgba(52, 211, 153, 0.28);
  box-shadow: 0 24px 48px rgba(0,0,0,0.35), 0 0 0 1px rgba(52, 211, 153, 0.08);
}
.candor-sso-cb__panel--error {
  border-color: rgba(248, 113, 113, 0.28);
}
.candor-sso-cb__marks {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  margin-bottom: 1.35rem;
  min-height: 3.5rem;
}
.candor-sso-cb__provider {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(0,0,0,0.25);
}
.candor-sso-cb__provider-icon {
  width: 2rem !important;
  height: 2rem !important;
}
.candor-sso-cb__spinner {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  border: 2.5px solid rgba(56, 189, 248, 0.2);
  border-top-color: var(--cb-accent);
  animation: candorSpin 0.75s linear infinite;
}
@keyframes candorSpin {
  to { transform: rotate(360deg); }
}
.candor-sso-cb__title {
  margin: 0;
  font-family: "Source Serif 4", Georgia, serif;
  font-size: 1.55rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #f8fafc;
  line-height: 1.25;
}
.candor-sso-cb__title--ok { color: #ecfdf5; }
.candor-sso-cb__title--err { color: #fecaca; }
.candor-sso-cb__copy {
  margin: 0.75rem auto 0;
  max-width: 22rem;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--cb-muted);
  font-weight: 400;
}
.candor-sso-cb__copy--err { color: #e2e8f0; }
.candor-sso-cb__next {
  margin: 1.15rem 0 0;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(148, 163, 184, 0.85);
}
.candor-sso-cb__steps {
  list-style: none;
  margin: 1.5rem 0 0;
  padding: 0;
  text-align: left;
  display: grid;
  gap: 0.45rem;
}
.candor-sso-cb__steps li {
  position: relative;
  padding: 0.45rem 0.75rem 0.45rem 1.85rem;
  font-size: 0.8rem;
  color: rgba(148, 163, 184, 0.75);
  border-radius: 0.5rem;
}
.candor-sso-cb__steps li::before {
  content: "";
  position: absolute;
  left: 0.65rem;
  top: 50%;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  transform: translateY(-50%);
  background: rgba(148, 163, 184, 0.35);
}
.candor-sso-cb__steps li.is-done {
  color: #a7f3d0;
}
.candor-sso-cb__steps li.is-done::before {
  background: var(--cb-ok);
}
.candor-sso-cb__steps li.is-active {
  color: #e0f2fe;
  background: rgba(56, 189, 248, 0.08);
}
.candor-sso-cb__steps li.is-active::before {
  background: var(--cb-accent);
  box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.15);
}
.candor-sso-cb__success-ring {
  width: 4.5rem;
  height: 4.5rem;
  margin: 0 auto 1.25rem;
}
.candor-sso-cb__check {
  width: 100%;
  height: 100%;
}
.candor-sso-cb__check-ring {
  stroke: rgba(52, 211, 153, 0.35);
  stroke-width: 2;
  fill: rgba(52, 211, 153, 0.08);
  animation: candorRing 0.5s ease-out both;
}
.candor-sso-cb__check-mark {
  stroke: var(--cb-ok);
  fill: none;
  stroke-dasharray: 36;
  stroke-dashoffset: 36;
  animation: candorDraw 0.55s 0.2s ease-out forwards;
}
@keyframes candorRing {
  from { opacity: 0; transform: scale(0.85); transform-origin: center; }
  to { opacity: 1; transform: scale(1); }
}
@keyframes candorDraw {
  to { stroke-dashoffset: 0; }
}
.candor-sso-cb__bar {
  margin: 1.35rem auto 0;
  width: 10rem;
  height: 3px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.15);
  overflow: hidden;
}
.candor-sso-cb__bar span {
  display: block;
  height: 100%;
  width: 40%;
  border-radius: inherit;
  background: linear-gradient(90deg, transparent, var(--cb-ok), transparent);
  animation: candorBar ${VERIFIED_HOLD_MS}ms linear forwards;
}
@keyframes candorBar {
  from { transform: translateX(-100%); }
  to { transform: translateX(280%); }
}
.candor-sso-cb__error-mark {
  width: 3.5rem;
  height: 3.5rem;
  margin: 0 auto 1.15rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cb-err);
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.22);
}
.candor-sso-cb__error-mark svg {
  width: 1.65rem;
  height: 1.65rem;
}
.candor-sso-cb__btn {
  margin-top: 1.25rem;
  appearance: none;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255,255,255,0.06);
  color: #f8fafc;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.65rem 1.25rem;
  border-radius: 0.65rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.candor-sso-cb__btn:hover {
  background: rgba(255,255,255,0.1);
  border-color: rgba(148, 163, 184, 0.55);
}
.candor-sso-cb__foot {
  margin-top: 1.25rem;
  text-align: center;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: rgba(100, 116, 139, 0.95);
}
@media (max-width: 420px) {
  .candor-sso-cb__panel { padding: 1.75rem 1.25rem; }
  .candor-sso-cb__title { font-size: 1.35rem; }
}
`;
